export function createConsentMatrix(data, sitters, movers, matchType = 'round-robin') {
  // Create a map for quick lookup of consents
  const consentMap = new Map(
    data
      .filter(u => u.userID && u.consents)
      .map(item => [item.userID, new Set(item.consents)])
  );

  // Initialize the matrix with nulls
  const matrix = movers.map(() => new Array(sitters.length).fill(null));

  // Fill the matrix
  movers.forEach((mover, moverIndex) => {
    sitters.forEach((sitter, sitterIndex) => {
      const moverConsents = consentMap.get(mover);
      const sitterConsents = consentMap.get(sitter);

      switch (matchType.toLowerCase()) {
        case 'mutual':
          // Both users must consent to each other
          if (
            moverConsents &&
            sitterConsents &&
            moverConsents.has(sitter) &&
            sitterConsents.has(mover)
          ) {
            matrix[moverIndex][sitterIndex] = moverIndex;
          }
          break;

        case 'movers':
          // Only mover's consent is required
          if (moverConsents && moverConsents.has(sitter)) {
            matrix[moverIndex][sitterIndex] = moverIndex;
          }
          break;

        case 'sitters':
          // Only sitter's consent is required
          if (sitterConsents && sitterConsents.has(mover)) {
            matrix[moverIndex][sitterIndex] = moverIndex;
          }
          break;

        case 'round-robin':
          // Everyone matches with everyone
          matrix[moverIndex][sitterIndex] = moverIndex;
          break;

        default:
          throw new Error('Invalid matchType. Use "mutual", "mover", or "sitter"');
      }
    });
  });

  return { consentMatrix: matrix, sitters, movers };
}

export function adjustColumns(oldMatrix, newMatrix) {
  // Get the new column count
  const newColCount = newMatrix[0]?.length;

  // Map through old matrix and just extend each row
  return oldMatrix.map(row => {
    // Create new row with existing values and pad with nulls
    const newRow = [...row];
    while (newRow.length < newColCount) {
      newRow.push(null);
    }
    return newRow;
  });
}

export function matrixDiff(oldMatrix, newMatrix) {
  oldMatrix = adjustColumns(oldMatrix, newMatrix);
  const result = [];

  // Determine the number of rows to iterate through
  const maxRows = Math.max(oldMatrix.length, newMatrix.length);

  for (let i = 0; i < maxRows; i++) {
    const oldRow = oldMatrix[i] || [];
    const newRow = newMatrix[i] || [];
    const maxCols = Math.max(oldRow.length, newRow.length);

    let rowDiffers = false;
    const diffRow = new Array(maxCols).fill(null);

    for (let j = 0; j < maxCols; j++) {
      if (i >= oldMatrix.length || j >= oldRow.length) {
        // New row or column added
        diffRow[j] = newRow[j];
        rowDiffers = true;
      } else if (i >= newMatrix.length || j >= newRow.length) {
        // Row or column removed
        diffRow[j] = null;
        rowDiffers = true;
      } else if (oldRow[j] !== newRow[j]) {
        // Value changed
        diffRow[j] = newRow[j];
        rowDiffers = true;
      }
    }

    if (rowDiffers) {
      result.push(diffRow);
    }
  }

  return result;
}

export function flattenMatrix(matrix) {
  const flattenedData = {};

  matrix.forEach((row, rowIndex) => {
    row.forEach((value, colIndex) => {
      if (value !== null) {
        flattenedData[`${rowIndex}_${colIndex}`] = value;
      }
    });
  });

  return flattenedData;
}

export function reconstructMatrix(flattenedMatrix) {
  const reconstructedArray = [];

  Object.entries(flattenedMatrix).forEach(([key, value]) => {
    const [row, col] = key.split("_").map(Number);

    if (!reconstructedArray[row]) {
      reconstructedArray[row] = [];
    }

    reconstructedArray[row][col] = value;
  });

  // Fill in any missing values with null
  const maxRow = Math.max(
    ...Object.keys(flattenedMatrix).map(k => parseInt(k.split("_")[0]))
  );
  const maxCol = Math.max(
    ...Object.keys(flattenedMatrix).map(k => parseInt(k.split("_")[1]))
  );

  for (let i = 0; i <= maxRow; i++) {
    if (!reconstructedArray[i]) {
      reconstructedArray[i] = [];
    }
    for (let j = 0; j <= maxCol; j++) {
      if (reconstructedArray[i][j] === undefined) {
        reconstructedArray[i][j] = null;
      }
    }
  }

  return reconstructedArray;
}

export function removeUnmatchedUsers(consentData, sitters, movers, matchType = 'round-robin') {
  // Helper functions for different types of matches
  const hasMutualMatch = (user1, user2) => {
    const user1Data = consentData.find(u => u.userID === user1);
    const user2Data = consentData.find(u => u.userID === user2);
    return user1Data && user2Data &&
      user1Data.consents.includes(user2) &&
      user2Data.consents.includes(user1);
  };

  const hasMoversMatch = (mover, sitter) => {
    const moverData = consentData.find(u => u.userID === mover);
    return moverData && moverData.consents.includes(sitter);
  };

  const hasSittersMatch = (sitter, mover) => {
    const sitterData = consentData.find(u => u.userID === sitter);
    return sitterData && sitterData.consents.includes(mover);
  };

  // Function to determine if there's a match based on matchType
  const hasMatch = (user1, user2, user1IsMover) => {
    switch (matchType.toLowerCase()) {
      case 'mutual':
        return hasMutualMatch(user1, user2);
      case 'movers':
        return user1IsMover ? hasMoversMatch(user1, user2) : hasMoversMatch(user2, user1);
      case 'sitters':
        return user1IsMover ? hasSittersMatch(user2, user1) : hasSittersMatch(user1, user2);
      case 'round-robin':
        return true; // Everyone matches with everyone
      default:
        throw new Error('Invalid matchType. Use "mutual", "movers", "sitters", or "all"');
    }
  };

  // If matchType is 'round-robin', return all users
  if (matchType.toLowerCase() === 'round-robin') {
    return {
      sitters: sitters,
      movers: movers
    };
  }

  // Find matches based on matchType
  const matchedSitters = sitters.filter(sitter =>
    movers.some(mover => hasMatch(sitter, mover, false))
  );

  const matchedMovers = movers.filter(mover =>
    sitters.some(sitter => hasMatch(mover, sitter, true))
  );

  return {
    sitters: matchedSitters,
    movers: matchedMovers
  };
}

/**
 * Transposes a matrix if movers outnumber sitters to optimize scheduling.
 * @param {Array<Array<number|null>>} matrix - The input consent matrix.
 * @param {number} originalNumMovers - Number of movers in the original matrix.
 * @param {number} originalNumSitters - Number of sitters in the original matrix.
 * @returns {Object} - Object containing the processed matrix and a boolean indicating if it was flipped.
 */
function transposeMatrixIfNeeded(matrix, originalNumMovers, originalNumSitters) {
  const isFlipped = originalNumMovers > originalNumSitters;
  if (isFlipped) {
    // Transpose the matrix so that movers are the smaller group
    const transposedMatrix = Array.from({ length: originalNumSitters }, () =>
      Array(originalNumMovers).fill(null)
    );
    for (let i = 0; i < originalNumMovers; i++) {
      for (let j = 0; j < originalNumSitters; j++) {
        if (matrix[i][j] !== null) {
          // The new "mover" is the old sitter, so the value should be the new mover's index (j)
          transposedMatrix[j][i] = j;
        }
      }
    }
    return { matrix: transposedMatrix, isFlipped };
  }
  return { matrix, isFlipped };
}

/**
 * Transposes an existing schedule if the matrix was flipped.
 * @param {Array<Array<number|null>>} schedule - The existing schedule.
 * @param {boolean} isFlipped - Whether the matrix is flipped.
 * @param {number} originalNumMovers - Number of movers in the original matrix.
 * @param {number} numSitters - Number of sitters in the current matrix.
 * @returns {Array<Array<number|null>>} - The adjusted and transposed schedule if needed.
 */
function adjustExistingSchedule(schedule, isFlipped, originalNumMovers, numSitters) {
  let adjustedSchedule = schedule.map(round => {
    if (round.length < numSitters) {
      return [...round, ...Array(numSitters - round.length).fill(null)];
    }
    return round.slice(0, numSitters);
  });

  if (isFlipped && adjustedSchedule.length > 0) {
    const transposedSchedule = [];
    for (let r = 0; r < adjustedSchedule.length; r++) {
      const round = adjustedSchedule[r];
      const newRound = Array(originalNumMovers).fill(null);
      for (let originalSitterIndex = 0; originalSitterIndex < round.length; originalSitterIndex++) {
        const originalMoverIndex = round[originalSitterIndex];
        if (originalMoverIndex !== null) {
          newRound[originalMoverIndex] = originalSitterIndex;
        }
      }
      transposedSchedule.push(newRound);
    }
    adjustedSchedule = transposedSchedule;
  }
  return adjustedSchedule;
}

/**
 * Un-transposes a schedule if the matrix was flipped and processes removed participants.
 * @param {Array<Array<number|null>>} schedule - The generated schedule.
 * @param {boolean} isFlipped - Whether the matrix is flipped.
 * @param {number} originalNumSitters - Number of sitters in the original matrix.
 * @param {number} currentRound - The round index from which scheduling started.
 * @param {Object} participants - Object containing lists of participants.
 * @returns {Array<Array<number|null>>} - The final processed schedule.
 */
function finalizeSchedule(schedule, isFlipped, originalNumSitters, currentRound = 0, participants = { removedMovers: [], removedSitters: [], movers: [], sitters: [] }) {
  let finalSchedule = schedule;
  if (isFlipped) {
    finalSchedule = [];
    for (const round of schedule) {
      const newRound = Array(originalNumSitters).fill(null);
      for (let originalMoverIndex = 0; originalMoverIndex < round.length; originalMoverIndex++) {
        const originalSitterIndex = round[originalMoverIndex];
        if (originalSitterIndex !== null) {
          newRound[originalSitterIndex] = originalMoverIndex;
        }
      }
      finalSchedule.push(newRound);
    }
  }

  // Process the schedule to nullify assignments for removed users
  return finalSchedule.map((row, rowIndex) => {
    if (rowIndex >= currentRound) {
      return row.map((value, colIndex) => {
        // Nullify assignments for removed sitters using the full sitter list index
        if (colIndex < participants.sitters.length && participants.removedSitters.includes(participants.sitters[colIndex])) {
          return null;
        }
        // Nullify assignments if the assigned mover is removed using the full mover list index
        if (value !== null && value < participants.movers.length && participants.removedMovers.includes(participants.movers[value])) {
          return null;
        }
        return value;
      });
    }
    return row;
  });
}

/**
 * Builds dance cards for movers based on the consent matrix.
 * @param {Array<Array<number|null>>} matrix - The consent matrix.
 * @param {number} numSitters - Number of sitters in the matrix.
 * @returns {Array<Array<number>>} - Shuffled dance cards for each mover.
 */
function buildDanceCards(matrix, numSitters) {
  const shuffle = (array) => {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  };

  return matrix.map(moverConsents => {
    const consentedSitters = [];
    for (let sitterIndex = 0; sitterIndex < numSitters; sitterIndex++) {
      if (moverConsents[sitterIndex] !== null) {
        consentedSitters.push(sitterIndex);
      }
    }
    return shuffle(consentedSitters);
  });
}

/**
 * Initializes completed meetings and wait times based on prior schedule.
 * @param {Array<Array<number|null>>} priorSchedule - Existing schedule.
 * @param {number} startRound - The round to start scheduling from.
 * @param {number} numMovers - Number of movers.
 * @param {number} numSitters - Number of sitters.
 * @param {Array<number>} sitterWaitTimes - Array to store sitter wait times.
 * @param {Array<number>} moverWaitTimes - Array to store mover wait times.
 * @param {Set<string>} completedMeetings - Set to store completed meeting pairs.
 */
function initializeFromPriorSchedule(priorSchedule, startRound, numMovers, numSitters, sitterWaitTimes, moverWaitTimes, completedMeetings) {
  if (priorSchedule.length > 0) {
    const roundsToConsider = Math.min(startRound, priorSchedule.length);
    for (let r = 0; r < roundsToConsider; r++) {
      const round = priorSchedule[r];
      for (let sitterIndex = 0; sitterIndex < round.length; sitterIndex++) {
        const moverIndex = round[sitterIndex];
        if (moverIndex !== null) {
          completedMeetings.add(`${moverIndex}-${sitterIndex}`);
        } else {
          sitterWaitTimes[sitterIndex]++;
        }
      }
      const scheduledMovers = new Set(round.filter(val => val !== null));
      for (let moverIndex = 0; moverIndex < numMovers; moverIndex++) {
        if (!scheduledMovers.has(moverIndex)) {
          moverWaitTimes[moverIndex]++;
        }
      }
    }
  }
}

/**
 * Builds a single round of the schedule.
 * @param {Array<Array<number>>} danceCards - Dance cards for movers.
 * @param {Array<number>} moverPointers - Current pointers for each mover's dance card.
 * @param {Array<number>} sitterWaitTimes - Wait times for sitters.
 * @param {Array<number>} moverWaitTimes - Wait times for movers.
 * @param {Set<string>} completedMeetings - Set of completed meeting pairs.
 * @param {number} numMovers - Number of movers.
 * @param {number} numSitters - Number of sitters.
 * @returns {Object} - Object containing the current round and a boolean indicating if new meetings were made.
 */
function buildScheduleRound(danceCards, moverPointers, sitterWaitTimes, moverWaitTimes, completedMeetings, numMovers, numSitters) {
  const currentRound = Array(numSitters).fill(null);
  const sittersTakenThisRound = new Set();
  let newMeetingsThisRound = 0;
  const moversScheduledThisRound = new Set();

  // Calculate available sitters for each mover and prioritize movers with fewer options
  const moverAvailability = [...Array(Math.min(numMovers, danceCards.length)).keys()].map(moverIndex => {
    const availableSitters = (danceCards[moverIndex] || []).filter(sitterIndex =>
      !completedMeetings.has(`${moverIndex}-${sitterIndex}`)
    );
    return {
      moverIndex,
      availableCount: availableSitters.length,
      wait: moverWaitTimes[moverIndex]
    };
  });

  // Sort movers by fewest available sitters, then by longest wait time, then randomly
  moverAvailability.sort((a, b) => {
    if (a.availableCount !== b.availableCount) return a.availableCount - b.availableCount;
    if (a.wait !== b.wait) return b.wait - a.wait;
    return Math.random() - 0.5;
  });

  for (const { moverIndex } of moverAvailability) {
    if (moverIndex >= danceCards.length) continue;
    const card = danceCards[moverIndex] || [];
    if (card.length === 0) continue;

    const potentialSitters = [];
    for (let i = 0; i < card.length; i++) {
      const sitterIndex = card[(moverPointers[moverIndex] + i) % card.length];
      if (!sittersTakenThisRound.has(sitterIndex) && !completedMeetings.has(`${moverIndex}-${sitterIndex}`)) {
        potentialSitters.push({
          sitterIndex,
          originalPos: (moverPointers[moverIndex] + i) % card.length,
          wait: sitterWaitTimes[sitterIndex]
        });
      }
    }

    if (potentialSitters.length > 0) {
      // Prioritize sitters by longest wait time, then randomly
      potentialSitters.sort((a, b) => {
        if (a.wait !== b.wait) return b.wait - a.wait;
        return Math.random() - 0.5;
      });

      const bestMatch = potentialSitters[0];
      const { sitterIndex, originalPos } = bestMatch;

      currentRound[sitterIndex] = moverIndex;
      sittersTakenThisRound.add(sitterIndex);
      completedMeetings.add(`${moverIndex}-${sitterIndex}`);
      sitterWaitTimes[sitterIndex] = 0;
      moversScheduledThisRound.add(moverIndex);
      newMeetingsThisRound++;

      moverPointers[moverIndex] = (originalPos + 1) % card.length;
    }
  }

  // Update wait times for unmatched entities
  for (let i = 0; i < numSitters; i++) {
    if (!sittersTakenThisRound.has(i)) {
      sitterWaitTimes[i]++;
    }
  }
  for (let i = 0; i < numMovers; i++) {
    if (!moversScheduledThisRound.has(i)) {
      moverWaitTimes[i]++;
    } else {
      moverWaitTimes[i] = 0;
    }
  }

  return { currentRound, newMeetingsThisRound };
}

/**
 * Internal function to run the round-robin scheduling algorithm.
 * @param {Array<Array<number|null>>} matrix - The consent matrix to base scheduling on.
 * @param {number} numMovers - Number of movers in the matrix.
 * @param {number} numSitters - Number of sitters in the matrix.
 * @param {number} startRound - The round to start scheduling from (default 0).
 * @param {Array<Array<number|null>>} priorSchedule - Existing schedule to preserve up to startRound.
 * @returns {Array<Array<number|null>>} - The generated schedule.
 */
function runRoundRobinScheduling(matrix, numMovers, numSitters, startRound = 0, priorSchedule = []) {
  const danceCards = buildDanceCards(matrix, numSitters);
  const moverPointers = Array(numMovers).fill(0);
  const sitterWaitTimes = Array(numSitters).fill(0);
  const moverWaitTimes = Array(numMovers).fill(0);
  const completedMeetings = new Set();

  initializeFromPriorSchedule(priorSchedule, startRound, numMovers, numSitters, sitterWaitTimes, moverWaitTimes, completedMeetings);

  const schedule = priorSchedule.slice(0, startRound);
  let roundsWithoutNewMeetings = 0;
  const maxPossibleMeetings = numMovers * numSitters;

  while (completedMeetings.size < maxPossibleMeetings && roundsWithoutNewMeetings < Math.max(numMovers, numSitters) * 2) {
    const { currentRound, newMeetingsThisRound } = buildScheduleRound(
      danceCards, moverPointers, sitterWaitTimes, moverWaitTimes, completedMeetings, numMovers, numSitters
    );

    if (currentRound.some(sitter => sitter !== null)) {
      schedule.push(currentRound);
    }

    if (newMeetingsThisRound === 0) {
      roundsWithoutNewMeetings++;
    } else {
      roundsWithoutNewMeetings = 0;
    }
  }

  return schedule;
}

/**
 * Generates a round-robin queue based on a consent matrix.
 * @param {Array<Array<number|null>>} originalMatrix - The input consent matrix.
 * @returns {Array<Array<number|null>>} - The generated schedule.
 */
export function generateQueueRoundRobin(originalMatrix) {
  if (!originalMatrix || originalMatrix.length === 0 || !originalMatrix[0]) return [];

  const originalNumMovers = originalMatrix.length;
  const originalNumSitters = originalMatrix[0].length;
  const { matrix, isFlipped } = transposeMatrixIfNeeded(originalMatrix, originalNumMovers, originalNumSitters);

  const numMovers = matrix.length;
  if (numMovers === 0) return [];
  const numSitters = matrix[0].length;

  // Run the scheduling algorithm with a reduced number of iterations and early stopping
  const results = [];
  const maxIterations = 20;
  let bestRounds = Infinity;

  for (let i = 0; i < maxIterations; i++) {
    const schedule = runRoundRobinScheduling(matrix, numMovers, numSitters);
    results.push(schedule);
    if (schedule.length < bestRounds) {
      bestRounds = schedule.length;
      // Early stopping if we find a schedule with minimal rounds
      if (bestRounds <= Math.ceil(numMovers / Math.min(numMovers, numSitters))) {
        break;
      }
    }
    // Early stopping if no improvement after a few iterations
    if (i > 3 && bestRounds === results[0].length) {
      break;
    }
  }

  // Find the schedule with the fewest rounds
  let bestSchedule = results[0];
  for (let i = 1; i < results.length; i++) {
    if (results[i].length < bestSchedule.length) {
      bestSchedule = results[i];
    }
  }

  return finalizeSchedule(bestSchedule, isFlipped, originalNumSitters);
}

/**
 * Generates a round-robin queue starting from a specified round, preserving earlier rounds.
 * @param {Array<Array<number|null>>} originalMatrix - The input consent matrix.
 * @param {number} currentRound - The round index from which to start scheduling.
 * @param {Array<Array<number|null>>} [existingSchedule] - Optional existing schedule to preserve up to currentRound - 1.
 * @param {Object} [participants] - Object containing lists of participants.
 * @param {Array<string>} [participants.removedMovers] - List of mover IDs that have been removed.
 * @param {Array<string>} [participants.removedSitters] - List of sitter IDs that have been removed.
 * @param {Array<string>} [participants.movers] - Full list of mover IDs for index mapping.
 * @param {Array<string>} [participants.sitters] - Full list of sitter IDs for index mapping.
 * @returns {Array<Array<number|null>>} - The generated schedule.
 */
export function generateQueueRoundRobinFromRound(originalMatrix, currentRound, existingSchedule = [], participants = { removedMovers: [], removedSitters: [], movers: [], sitters: [] }) {
  if (!originalMatrix || originalMatrix.length === 0 || !originalMatrix[0]) return [];

  const originalNumMovers = originalMatrix.length;
  const originalNumSitters = originalMatrix[0].length;
  const { matrix, isFlipped } = transposeMatrixIfNeeded(originalMatrix, originalNumMovers, originalNumSitters);

  const numMovers = matrix.length;
  if (numMovers === 0) return [];
  const numSitters = matrix[0].length;

  const adjustedExistingSchedule = adjustExistingSchedule(existingSchedule, isFlipped, originalNumMovers, numSitters);

  // Run the scheduling algorithm with a reduced number of iterations and early stopping
  const results = [];
  const maxIterations = 20;
  let bestRounds = Infinity;

  for (let i = 0; i < maxIterations; i++) {
    const schedule = runRoundRobinScheduling(matrix, numMovers, numSitters, currentRound, adjustedExistingSchedule);
    if (schedule.length >= currentRound) {
      let valid = true;
      for (let r = 0; r < currentRound && r < adjustedExistingSchedule.length; r++) {
        if (JSON.stringify(schedule[r]) !== JSON.stringify(adjustedExistingSchedule[r])) {
          valid = false;
          break;
        }
      }
      if (valid) {
        results.push(schedule);
        if (schedule.length < bestRounds) {
          bestRounds = schedule.length;
          // Early stopping if we find a schedule with minimal rounds
          if (bestRounds <= Math.ceil(numMovers / Math.min(numMovers, numSitters)) + currentRound) {
            break;
          }
        }
        // Early stopping if no improvement after a few iterations
        if (i > 3 && results.length > 1 && bestRounds === results[0].length) {
          break;
        }
      }
    }
  }

  // If no valid schedules found, use the first one or adjustedExistingSchedule up to currentRound
  if (results.length === 0 && adjustedExistingSchedule.length >= currentRound) {
    results.push(runRoundRobinScheduling(matrix, numMovers, numSitters, currentRound, adjustedExistingSchedule));
  }

  // Find the schedule with the fewest rounds among valid schedules
  let bestSchedule = results[0];
  for (let i = 1; i < results.length; i++) {
    if (results[i].length < bestSchedule.length) {
      bestSchedule = results[i];
    }
  }

  return finalizeSchedule(bestSchedule, isFlipped, originalNumSitters, currentRound, participants);
}
